import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import WebhookEventStatusSticker from 'dashboard/components/WebhookEventStatusSticker';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { WebhookEvent } from 'dashboard/models/webhook-event';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';

const WebhookEventsTableContainer = styled('div')`
  margin-bottom: ${Spacing[2]};
`;

interface WebhookEventsTableProps {
  hasUsedFilters: boolean;
  loading: boolean;
  webhookEvents: WebhookEvent[];
  dataTest: string;
  onRowClick: (wbhe: WebhookEvent) => void;
  nullStateMessage: string;
  // Whether this is rendered from the perspective of a webhook or an event
  context: 'webhook' | 'event';
}

/**
 * Webhook events table to be used in both Events and Webhooks show view.
 */
const WebhookEventsTable = ({
  hasUsedFilters,
  loading,
  webhookEvents,
  dataTest,
  onRowClick,
  nullStateMessage,
  context,
}: WebhookEventsTableProps) => {
  return (
    <WebhookEventsTableContainer data-test={dataTest}>
      <TableLayout
        dataTest='webhook-events-table__table'
        isEmpty={webhookEvents.length === 0}
        isLoading={loading}
        showHeader={hasUsedFilters}
        headerTR={
          <>
            <TableTH>Status (Code)</TableTH>
            <TableTH>{context === 'event' ? 'Webhook URL' : 'Event name'}</TableTH>
            <TableTH>{`Last attempted at (${DateTime.local().toFormat(
              TIMEZONE_NAME_ABBREVIATED,
            )})`}</TableTH>
            <TableTH>Attempts Left</TableTH>
          </>
        }
        nullState={
          <TableNullState hasUsedFilters={hasUsedFilters} dataTestBlock='webhook-events-table'>
            {nullStateMessage}
          </TableNullState>
        }
      >
        {webhookEvents.map((webhookEvent) => {
          const {
            'updated-at': updatedAt,
            'event-name': eventName,
            'webhook-url': webhookUrl,
            'attempts-left': attemptsLeft,
          } = webhookEvent.attributes;
          return (
            <TableTR
              key={webhookEvent.id}
              data-test={`webhook-events-table-row__${webhookEvent.id}`}
              id={`webhook-events-table-row__${webhookEvent.id}`}
              onClick={() => onRowClick(webhookEvent)}
            >
              <TableTD>
                <WebhookEventStatusSticker webhookEvent={webhookEvent} />
              </TableTD>
              <TableTD>{context === 'event' ? webhookUrl : eventName}</TableTD>
              <TableTD>{DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
              <TableTD>{attemptsLeft}</TableTD>
            </TableTR>
          );
        })}
      </TableLayout>
    </WebhookEventsTableContainer>
  );
};

export default WebhookEventsTable;
